import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email, password);
      toast.success(`Welcome back, ${u.full_name}`);
      nav(u.role === "patient" ? "/patient" : u.role === "lab_admin" ? "/lab" : "/admin");
    } catch (err) {
      toast.error(apiError(err, "Login failed"));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex bg-ink text-white p-12 flex-col justify-between">
        <Link to="/" className="flex items-center">
          <img src="/efgh-logo-white.png" alt="EFGH" className="h-12" />
        </Link>
        <div>
          <h2 className="font-heading font-bold text-4xl leading-tight">Your health,<br />clearly explained.</h2>
          <p className="mt-4 text-white/60 max-w-md">Sign in to view AI smart reports, listen to audio summaries, and book follow-up tests in minutes.</p>
        </div>
        <div className="text-white/40 text-xs">© 2026 EFGH Health Technologies</div>
      </div>
      <div className="p-4 sm:p-8 lg:p-12 flex flex-col items-center justify-center">
        <Link to="/" className="lg:hidden mb-6 flex items-center" data-testid="login-mobile-logo">
          <img src="/efgh-logo.png" alt="EFGH" className="h-11" />
        </Link>
        <Card className="w-full max-w-md p-6 sm:p-8 rounded-2xl border-border">
          <h1 className="font-heading font-bold text-2xl text-ink">Welcome back</h1>
          <p className="mt-1 text-sm text-ink/60">Sign in to your EFGH account</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <div><Label>Email</Label><Input data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></div>
            <div><Label>Password</Label><Input data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required /></div>
            <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full h-11 bg-brand hover:bg-brand-dark text-white">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
            <div className="text-right">
              <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm text-ink/60 hover:text-ink underline underline-offset-4">
                Forgot password?
              </Link>
            </div>
          </form>
          <div className="mt-6 text-sm text-center text-ink/60">
            Are you a diagnostic lab? <Link to="/for-labs" className="text-brand font-medium">Join EFGH</Link>
          </div>
          <div className="mt-2 text-sm text-center text-ink/60">
            Patient? <Link to="/register" className="text-brand font-medium">Create account</Link>
          </div>
        </Card>
      </div>
    </div>
  );
}
